import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext, useEffect, useState } from 'react';
import { Box, Paper, Typography, Grid } from '@mui/material';
import { MailOutline } from '@mui/icons-material';
import { sendSignInLinkToEmail, GoogleAuthProvider, signInWithPopup, signInWithRedirect, } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import { Crisp } from 'crisp-sdk-web';
// Internal dependencies
import { ButtonSG, MiddleLineSG, GoogleSignInIconSG, FirebaseLogoSG } from '../components';
import { studioGlobal } from '../assets/logos';
import { UserContext, auth } from '../auth';
import { EMAIL_LINK_STAGES } from '../constants/emailLinkStages';
import { styles } from './styles';
import { EmailForm } from './EmailForm';
import { CLIENT_ENDPOINTS, ENDPOINTS, LOG_LEVELS, SERVICES } from '../constants';
import { axiosClient } from '../axios';
;
/**
 * Sign In Element
 * @param {any} props
 * @returns {JSX.Element}
 */
export const SignIn = (props) => {
    const { state } = useLocation();
    const { t, i18n } = useTranslation();
    // email
    const [email, setEmail] = useState('');
    // Email link sign in stage
    const [stage, setStage] = useState(EMAIL_LINK_STAGES.INPUT_EMAIL);
    // Get redirect path
    let path = '/';
    if (state === null || state === void 0 ? void 0 : state.pathname) {
        path = `${state === null || state === void 0 ? void 0 : state.pathname}`;
    }
    if (state === null || state === void 0 ? void 0 : state.search) {
        path = `${path}${state === null || state === void 0 ? void 0 : state.search}`;
    }
    ;
    /**
     * Handle email submit to get a magic link
     * @param {string} email
     * @returns {void}
     */
    const onSubmit = (email) => {
        try {
            // Firebase action code settings
            const actionCodeSettings = {
                url: `${window.location.href}${CLIENT_ENDPOINTS.VERIFY_MAGIC_LINK}?path=${path}`,
                handleCodeInApp: true,
            };
            // Set email for resend action
            setEmail(email);
            // Set language before sending an email
            auth.languageCode = i18n.language;
            // Firebase sendSignInLinkToEmail
            sendSignInLinkToEmail(auth, email, actionCodeSettings).then(() => {
                // Set user email to Crisp
                Crisp.user.setEmail(email);
                // Change stage to Sent Email
                setStage(EMAIL_LINK_STAGES.SENT_EMAIL);
                // Save email to local storage for link verification stage
                window.localStorage.setItem('emailForSignIn', email);
            }).catch((err) => {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `SignIn, onSubmit, sendSignInLinkToEmail, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.SIGN_IN,
                        userId: null,
                        email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
                // Notify error to user
                toast.error(t('Something went wrong. We are figuring it out.'));
            });
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `SignIn, onSubmit, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            // Notify error to user
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    };
    // React Router Dom navigate hook
    const navigate = useNavigate();
    // Authorized user context
    const user = useContext(UserContext);
    // Navigate to specific path or root when user signed in
    useEffect(() => {
        if (user) {
            Crisp.user.setEmail(user.email || '');
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.INFO,
                logMessage: `SignIn Sucess, ${user.email}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.SIGN_IN,
                    userId: null,
                    email: user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            navigate(`${path}`);
        }
        ;
    }, [user]);
    // Title, change title when stage changed
    let title = 'Continue with email';
    if (stage === EMAIL_LINK_STAGES.SENT_EMAIL) {
        title = 'Verify your email';
    }
    ;
    // Subtitle, change subtitle when stage changed
    let subtitle = 'Enter your email address to log in or sign up in seconds.';
    if (stage === EMAIL_LINK_STAGES.SENT_EMAIL) {
        subtitle = `We’ve sent an email to {{email}}.<br>Click the activation link inside and get started! 🤟`;
    }
    ;
    /**
     * Handle resend action
     */
    const handleResendClick = () => {
        if (email) {
            onSubmit(email.toLowerCase());
        }
        else {
            navigate(CLIENT_ENDPOINTS.SIGN_IN);
        }
        ;
    };
    // Initialize Google provider
    const googleProvider = new GoogleAuthProvider();
    /**
     * Handle Sign in with google
     */
    const handleSignInWithGoogle = () => {
        signInWithPopup(auth, googleProvider)
            .then((result) => {
            // Set user email on Crisp
            Crisp.user.setEmail(result.user.email || '');
        })
            .catch((err) => {
            signInWithRedirect(auth, googleProvider)
                .then((result) => {
            })
                .catch((err) => {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `SignIn, handleSignInWithGoogle, ${err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.SIGN_IN,
                        userId: null,
                        email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
                toast.error(t('Pop-ups are currently disabled in your browser settings. Please enable it and try again.'));
            });
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `SignIn, handleSignInWithGoogle, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.SIGN_IN,
                    userId: null,
                    email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        });
    };
    /** Handle email submit click */
    const handleSubmit = (email) => {
        onSubmit(email.toLowerCase());
    };
    return (_jsx(Box
    // Sign In container
    , Object.assign({ 
        // Sign In container
        sx: styles.container }, { children: _jsx(Box, Object.assign({ sx: {
                width: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                height: '100%',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                overflow: 'auto',
            } }, { children: _jsxs(Paper, Object.assign({ sx: styles.paper }, { children: [_jsx(Box
                    // Logo block
                    , Object.assign({ 
                        // Logo block
                        sx: styles.logo }, { children: _jsx("img", { src: studioGlobal, height: "40px", alt: "Studio Global Logo" }) })), _jsx(Typography
                    // Title
                    , Object.assign({ 
                        // Title
                        sx: styles.title, component: "h1" }, { children: t(title) })), _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, styles.subtitle), { display: stage === EMAIL_LINK_STAGES.INPUT_EMAIL ? 'inline-flex' : 'none' }) }, { children: t('Log in or sign up to use smarter AI models') })), _jsx(ButtonSG
                    // Sign In with Google button
                    , Object.assign({ 
                        // Sign In with Google button
                        variant: "contained", xstyles: {
                            backgroundColor: '#000000',
                            borderRadius: '100px',
                            width: '100%',
                            margin: '0 0 1rem',
                            display: stage === EMAIL_LINK_STAGES.INPUT_EMAIL ? 'flex' : 'none',
                        }, onClick: (e) => { e.preventDefault(); handleSignInWithGoogle(); }, startIcon: _jsx(GoogleSignInIconSG, { sx: { width: '1.1875rem', height: '1.125rem' } }) }, { children: t('Continue with Google') })), _jsx(MiddleLineSG, Object.assign({ xstyles: {
                            display: stage === EMAIL_LINK_STAGES.INPUT_EMAIL ? 'block' : 'none',
                        } }, { children: t('Or') })), _jsx(Typography
                    // Subtitle
                    , Object.assign({ 
                        // Subtitle
                        sx: styles.subtitle }, { children: _jsx(Trans, { i18nKey: subtitle, defaults: subtitle, values: { email: email } }) })), _jsx(MiddleLineSG, Object.assign({ xstyles: {
                            display: stage === EMAIL_LINK_STAGES.SENT_EMAIL ? 'block' : 'none',
                        } }, { children: t('Couldn’t receive email?') })), _jsx(Grid, Object.assign({ container: true, sx: {
                            justifyContent: 'center',
                            display: stage === EMAIL_LINK_STAGES.SENT_EMAIL ? 'flex' : 'none',
                        } }, { children: _jsx(Grid, Object.assign({ item: true }, { children: _jsx(ButtonSG
                            // Resend magic link button
                            , Object.assign({ 
                                // Resend magic link button
                                color: "secondary", variant: "contained", xstyles: styles.resendButton, onClick: handleResendClick, startIcon: _jsx(MailOutline, { sx: { color: '#0A1F16', } }) }, { children: t('Resend') })) })) })), _jsx(Box
                    // Email form block
                    , Object.assign({ 
                        // Email form block
                        sx: { width: '100%', display: stage === EMAIL_LINK_STAGES.SENT_EMAIL ? 'none' : 'block', margin: '0 0 1rem' } }, { children: _jsx(EmailForm, { stage: stage, onSubmit: handleSubmit }) })), _jsxs(Grid, Object.assign({ container: true, columnSpacing: 0.5, sx: { alignItems: 'center', justifyContent: 'flex-end' } }, { children: [_jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(Typography, Object.assign({ sx: { fontSize: '11px', lineHeight: '1rem', color: 'blackDark.main', fontWeight: '500' } }, { children: t('Secured by') })) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(FirebaseLogoSG, { sx: { width: '57px', height: '16px' } }) }))] }))] })) })) })));
};
